/**
 * RSVP audiences decided by the SERVER at send time (OrchestratorAPI #394).
 *
 * Until #394, Studio resolved "No reply yet" / "RSVP'd yes" in the browser into
 * a FROZEN list of invitee ids, and a scheduled send carried that list until it
 * fired: a guest who replied after the host scheduled the chase was chased
 * anyway. `audienceKind` moves the definition to the moment of sending.
 *
 * Two wire rules, both enforced by the API with a 400:
 *
 * - `audienceKind` is mutually exclusive with `inviteeIds`, `segment` and
 *   `tagSlug`. Ids resolved a moment ago are exactly what a chase must NOT be
 *   built from, so the two never travel together — see {@link audienceWireFields}.
 * - An API that predates #394 does not reject the field, it IGNORES it (ASP.NET
 *   drops unknown JSON members). For "No reply yet" that would mean an
 *   audience-less send: the whole roster. So Studio never sends `audienceKind`
 *   on faith — it asks first ({@link probeAudienceKindSupport}) and falls back
 *   to today's frozen ids until the server has proved it understands the field.
 *
 * Pure and dependency-light on purpose (no React, no axios) so the wire rules
 * are unit-tested in `audienceKind.test.ts`.
 */
import { mutationErrorMessage } from './mutation-error';
import {
  AUDIENCE_RSVP_GOING,
  AUDIENCE_RSVP_NO_REPLY,
  audienceInviteeIds,
  audienceSegment,
} from '../components/groups/broadcasts/audience';
import type {
  GroupScheduledBroadcast,
  ScheduleGroupBroadcastRequest,
  SendGroupBroadcastRequest,
} from '../types/groups';
import type { TrackerGuest } from '../services/tracker-api-client';

export type AudienceKind = 'rsvp_no_reply' | 'rsvp_going';

/** What the host is told when the server cannot decide at send time. Shown verbatim. */
export const AUDIENCE_KIND_FALLBACK_NOTE =
  "This server can't decide at send time yet — using today's list.";

/** The wire kind for a composer audience value, or null when the audience is not an RSVP one. */
export function audienceKindFor(audience: string): AudienceKind | null {
  if (audience === AUDIENCE_RSVP_NO_REPLY) return 'rsvp_no_reply';
  if (audience === AUDIENCE_RSVP_GOING) return 'rsvp_going';
  return null;
}

/** Host-facing name for a wire kind (the same words the composer's audience menu uses). */
export function audienceKindLabel(kind: string | null | undefined): string | null {
  if (kind === 'rsvp_no_reply') return 'No reply yet';
  if (kind === 'rsvp_going') return "RSVP'd yes";
  return null;
}

/** Audience fields of a broadcast request: exactly one of `audienceKind` OR ids/segment. */
export interface AudienceWireFields {
  audienceKind?: AudienceKind;
  segment?: string | null;
  inviteeIds?: string[];
}

/**
 * The audience half of an interactive-send or schedule request.
 *
 * RSVP audience + a server that has proved it understands `audienceKind` →
 * `{ audienceKind }` and NOTHING else (no `inviteeIds`, no `segment`: the API
 * 400s on the combination, and an id list would reintroduce the frozen answer).
 * Anything else → today's shape (a segment, or the frozen id list) — which is
 * also the fallback when the server has not proved support.
 */
export function audienceWireFields(
  audience: string,
  guests: ReadonlyArray<TrackerGuest> | null | undefined,
  serverDecides: boolean,
): AudienceWireFields {
  const kind = audienceKindFor(audience);
  if (kind && serverDecides) return { audienceKind: kind };
  return {
    segment: audienceSegment(audience),
    // [] must stay [] — undefined means "no id filter" and the API reads that as everyone.
    inviteeIds: audienceInviteeIds(audience, guests),
  };
}

const status = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * True for the two refusals that mean "this server cannot do send-time
 * audiences": a 400 that names `audienceKind`, or the 503
 * `reminder_sequences_unavailable` designed state (migration 016 not applied).
 * Both are answered by falling back to frozen ids, never by a red error.
 */
export function isAudienceKindRejected(error: unknown): boolean {
  return isReminderSequencesUnavailable(error)
    || (status(error) === 400 && /audienceKind/i.test(mutationErrorMessage(error)));
}

/** The 503 designed state for reminder plans / send-time audiences — migration 016 not applied yet. */
export function isReminderSequencesUnavailable(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return response?.status === 503 && response?.data?.code === 'reminder_sequences_unavailable';
}

/** A value no real API accepts as an `audienceKind`, used only by the probe below. */
export const PROBE_AUDIENCE_KIND = 'studio_capability_probe';

/**
 * Does this server understand `audienceKind`?
 *
 * Sends a DRY RUN carrying a deliberately invalid kind. An API with #394
 * validates the kind before doing anything else and answers 400 "Unsupported
 * audienceKind …"; an API without it ignores the field and answers the dry run
 * normally (or refuses for an unrelated reason). Only the first proves support.
 * Nothing is sent to anyone either way — `dryRun: true` — and every non-proof
 * (network failure, 401, unrelated 400) reads as "not supported", the safe
 * direction: it falls back to today's frozen ids.
 */
export async function probeAudienceKindSupport(
  send: (request: SendGroupBroadcastRequest) => Promise<unknown>,
  options: { throwOnTransient?: boolean } = {},
): Promise<boolean> {
  try {
    await send({
      subject: 'capability probe',
      body: 'capability probe',
      channels: ['email'],
      audienceKind: PROBE_AUDIENCE_KIND,
      dryRun: true,
    });
    return false;
  } catch (error) {
    // A caller that CACHES the answer must not cache a blip as "unsupported":
    // it asks for transient failures to be thrown so its query retries.
    if (options.throwOnTransient && isTransientProbeError(error)) throw error;
    return status(error) === 400 && /unsupported audienceKind/i.test(mutationErrorMessage(error));
  }
}

/**
 * A probe failure that says nothing about the server's capabilities: no answer
 * at all (offline, timeout), an auth blip (401/403), throttling (408/429), or
 * a server error that is not the designed 503. A definitive answer — 200, any
 * 400, 404, or the designed 503 — is NOT transient.
 */
export function isTransientProbeError(error: unknown): boolean {
  const code = status(error);
  if (code === undefined) return true;
  if (isReminderSequencesUnavailable(error)) return false;
  return code === 401 || code === 403 || code === 408 || code === 429 || code >= 500;
}

/**
 * Schedule with a send-time audience, falling back to today's frozen ids if the
 * server turns out not to support it (400 naming `audienceKind`, or the 503
 * `reminder_sequences_unavailable` state: migration 016 not applied). Nothing is
 * created by the refused call, so retrying with the frozen request cannot
 * double-schedule. Any OTHER failure propagates untouched.
 */
export async function scheduleWithAudienceFallback(
  schedule: (request: ScheduleGroupBroadcastRequest) => Promise<GroupScheduledBroadcast>,
  kindRequest: ScheduleGroupBroadcastRequest,
  frozenRequest: ScheduleGroupBroadcastRequest,
): Promise<{ row: GroupScheduledBroadcast; fellBack: boolean }> {
  if (!kindRequest.audienceKind) return { row: await schedule(frozenRequest), fellBack: false };
  try {
    return { row: await schedule(kindRequest), fellBack: false };
  } catch (error) {
    if (!isAudienceKindRejected(error)) throw error;
    return { row: await schedule(frozenRequest), fellBack: true };
  }
}
